// @ts-nocheck
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "../components/layout/PageHeader";
import { StatCard } from "../components/StatCard";
import { useLang } from "../lib/lang";

const API = import.meta.env.VITE_API_URL ?? "";

interface WebhookLog {
  id: number; event: string; url: string; status: "success" | "failed" | "pending"; responseCode: number; payload: string | null; createdAt: string;
}

async function fetchLogs(status: string): Promise<WebhookLog[]> {
  const r = await fetch(`${API}/api/webhook-logs?status=${status}`); return r.json();
}

const statusStyle = (status: string) => {
  const map: Record<string, string> = { success: "bg-[#d4edda] text-[#155724]", failed: "bg-[#f8d7da] text-[#721c24]", pending: "bg-[#fff3cd] text-[#856404]" };
  return map[status] ?? "bg-[#e9ecef] text-[#525f7f]";
};

export function Webhooks() {
  const qc = useQueryClient();
  const { t } = useLang();
  const d = t.webhooks;

  const [filterStatus, setFilterStatus] = useState("all");
  const [showSetup, setShowSetup] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["webhook-logs", filterStatus],
    queryFn: () => fetchLogs(filterStatus),
    refetchInterval: 15000,
  });
  const retry = useMutation({
    mutationFn: async (id: number) => { const r = await fetch(`${API}/api/webhook-logs/retry/${id}`, { method: "POST" }); return r.json(); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["webhook-logs"] }),
  });

  const successCount = logs.filter(l => l.status === "success").length;
  const failedCount = logs.filter(l => l.status === "failed").length;
  const pendingCount = logs.filter(l => l.status === "pending").length;

  const filterKeys = ["all", "success", "failed", "pending"] as const;

  return (
    <div>
      <PageHeader title={d.title} actions={[{ label: d.configure, icon: "fas fa-cog", onClick: () => setShowSetup(!showSetup) }]} />

      <div className="px-6 pb-6 space-y-6">
        <div className="grid grid-cols-3 gap-4">
          <StatCard label={d.successCount} value={successCount} iconClass="fas fa-check-circle" gradient="success" />
          <StatCard label={d.failedCount} value={failedCount} iconClass="fas fa-times-circle" gradient="danger" />
          <StatCard label={d.pendingCount} value={pendingCount} iconClass="fas fa-clock" gradient="warning" />
        </div>

        {showSetup && (
          <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef] p-5">
            <h4 className="text-sm font-semibold text-[#32325d] mb-3">{d.setupTitle}</h4>
            <p className="text-xs text-[#8898aa] mb-4">{d.setupHint}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-[#525f7f] mb-1.5">{d.webhookUrlLabel}</label>
                <div className="flex gap-2">
                  <input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} className="flex-1 border border-[#cad1d7] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1171ef]/30 focus:border-[#1171ef]" placeholder="https://yourapp.com/webhook" />
                  <button className="gradient-primary text-white px-4 py-2 rounded-lg text-sm shadow-sm hover:opacity-90">{d.save}</button>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold text-[#525f7f] mb-2">Events</p>
                <div className="grid grid-cols-3 gap-2">
                  {["message.sent", "message.delivered", "message.read", "message.failed", "device.connected", "device.disconnected"].map(evt => (
                    <label key={evt} className="flex items-center gap-2 text-xs text-[#525f7f] cursor-pointer">
                      <input type="checkbox" defaultChecked className="rounded" />
                      <code className="bg-[#f8f9fe] px-1 rounded">{evt}</code>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-[#e9ecef]">
          <div className="px-5 py-4 border-b border-[#e9ecef] flex items-center justify-between">
            <h4 className="text-sm font-semibold text-[#32325d]">{d.title}</h4>
            <div className="flex gap-2">
              {filterKeys.map(s => (
                <button key={s} onClick={() => setFilterStatus(s)} className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${filterStatus === s ? "gradient-primary text-white" : "bg-[#e9ecef] text-[#525f7f] hover:bg-[#dee2e6]"}`}>
                  {d.filter[s]}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="p-12 text-center"><i className="fas fa-spinner fa-spin text-2xl text-[#1171ef]" /></div>
          ) : logs.length === 0 ? (
            <div className="p-12 text-center">
              <i className="fas fa-paper-plane text-4xl text-[#cad1d7] mb-3 block" />
              <p className="text-[#8898aa] text-sm">{d.noLogs}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    {[d.event, d.url, d.code, t.common.status, t.common.date, t.common.actions].map(h => (
                      <th key={h} className="px-5 py-3 text-start text-xs font-semibold text-[#8898aa] uppercase tracking-wider border-b border-[#e9ecef]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map(log => (
                    <tr key={log.id} className="border-b border-[#f6f9fc] hover:bg-[#f8f9fe]">
                      <td className="px-5 py-3"><code className="text-xs bg-[#e9ecef] px-2 py-0.5 rounded font-mono text-[#525f7f]">{log.event}</code></td>
                      <td className="px-5 py-3 text-xs text-[#525f7f] max-w-[180px] truncate">{log.url}</td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-mono font-bold ${log.responseCode >= 200 && log.responseCode < 300 ? "text-[#2dce89]" : log.responseCode >= 400 ? "text-[#f5365c]" : "text-[#8898aa]"}`}>
                          {log.responseCode || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${statusStyle(log.status)}`}>
                          {d.filter[log.status as keyof typeof d.filter] ?? log.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-[#8898aa]">{new Date(log.createdAt).toLocaleString()}</td>
                      <td className="px-5 py-3">
                        <button onClick={() => retry.mutate(log.id)} disabled={retry.isPending} className="text-xs text-[#1171ef] hover:underline disabled:opacity-50">
                          <i className="fas fa-redo-alt me-1" />{d.retry}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
